import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

type Phase = 'typing' | 'generating' | 'result' | 'reset';

const TYPING_TEXT = 'Coffee shop in NYC targeting busy morning commuters...';

const GENERATED_AD = {
  headline: 'Your Morning Fuel, Perfected.',
  body: 'Skip the line. Sip something better. Fresh-brewed specialty coffee made for busy New Yorkers — order ahead and grab it on your way.',
  cta: 'Order Now',
  platform: 'Facebook Ad',
  score: 94,
};

const PLATFORM_ICONS: Record<string, string> = {
  'Facebook Ad': 'ri-facebook-fill',
};

function AnimatedDemo() {
  const [phase, setPhase] = useState<Phase>('typing');
  const [typedText, setTypedText] = useState('');
  const [charIndex, setCharIndex] = useState(0);
  const [resultVisible, setResultVisible] = useState(false);
  const [dotsCount, setDotsCount] = useState(0);
  const phaseRef = useRef<Phase>('typing');
  phaseRef.current = phase;

  // Typing animation
  useEffect(() => {
    if (phase !== 'typing') return;
    if (charIndex < TYPING_TEXT.length) {
      const t = setTimeout(() => {
        setTypedText(TYPING_TEXT.slice(0, charIndex + 1));
        setCharIndex((c) => c + 1);
      }, 48);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setPhase('generating'), 700);
    return () => clearTimeout(t);
  }, [phase, charIndex]);

  // Generating dots animation
  useEffect(() => {
    if (phase !== 'generating') return;
    const interval = setInterval(() => {
      setDotsCount((d) => (d + 1) % 4);
    }, 350);
    const done = setTimeout(() => {
      clearInterval(interval);
      setPhase('result');
    }, 2400);
    return () => {
      clearInterval(interval);
      clearTimeout(done);
    };
  }, [phase]);

  // Result fade-in then reset
  useEffect(() => {
    if (phase !== 'result') {
      setResultVisible(false);
      return;
    }
    const show = setTimeout(() => setResultVisible(true), 60);
    const reset = setTimeout(() => setPhase('reset'), 4200);
    return () => {
      clearTimeout(show);
      clearTimeout(reset);
    };
  }, [phase]);

  // Reset cycle
  useEffect(() => {
    if (phase !== 'reset') return;
    setTypedText('');
    setCharIndex(0);
    setResultVisible(false);
    setDotsCount(0);
    const t = setTimeout(() => setPhase('typing'), 400);
    return () => clearTimeout(t);
  }, [phase]);

  return (
    <div className="mt-12 max-w-2xl mx-auto">
      <div className="relative bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden">
        {/* Demo header bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/15 bg-white/5">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
          </div>
          <span className="text-white/50 text-xs font-medium tracking-wide">AdForge — Live Demo</span>
          <div className="w-16" />
        </div>

        <div className="p-5 space-y-4">
          {/* Input section */}
          <div>
            <label className="block text-white/60 text-xs font-semibold uppercase tracking-widest mb-2">
              Describe your business
            </label>
            <div className="relative bg-white/10 border border-white/20 rounded-xl px-4 py-3 min-h-[52px] flex items-center">
              <span className="text-white text-sm leading-relaxed">
                {typedText}
                {(phase === 'typing') && (
                  <span className="inline-block w-0.5 h-4 bg-orange-400 ml-0.5 animate-pulse align-middle" />
                )}
              </span>
            </div>
          </div>

          {/* Platform selector */}
          <div className="flex items-center gap-3">
            <span className="text-white/60 text-xs font-semibold uppercase tracking-widest">Platform:</span>
            {['Facebook', 'Instagram', 'Google'].map((p) => (
              <span
                key={p}
                className={`text-xs font-semibold px-3 py-1 rounded-full border transition-all duration-300 ${
                  p === 'Facebook'
                    ? 'bg-orange-500/30 border-orange-400/50 text-orange-300'
                    : 'bg-white/5 border-white/15 text-white/40'
                }`}
              >
                {p}
              </span>
            ))}
          </div>

          {/* Generate button */}
          <button
            type="button"
            className={`w-full py-3 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 cursor-default ${
              phase === 'generating'
                ? 'bg-orange-500/60 text-white/80'
                : phase === 'result'
                ? 'bg-green-500/40 border border-green-400/40 text-green-300'
                : 'bg-orange-500/80 hover:bg-orange-500 text-white'
            }`}
          >
            {phase === 'generating' && (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line text-white animate-spin text-base" />
                </div>
                <span>Generating your ad{'.'.repeat(dotsCount)}</span>
              </>
            )}
            {phase === 'result' && (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-green-300 text-base" />
                </div>
                <span>Ad Generated!</span>
              </>
            )}
            {(phase === 'typing' || phase === 'reset') && (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-sparkling-fill text-white text-base" />
                </div>
                <span>Generate Ad with AI</span>
              </>
            )}
          </button>

          {/* Result card */}
          <div
            className={`transition-all duration-700 overflow-hidden ${
              resultVisible ? 'max-h-[200px] opacity-100 translate-y-0' : 'max-h-0 opacity-0 translate-y-2'
            }`}
            style={{ transitionProperty: 'max-height, opacity, transform' }}
          >
            <div className="bg-white/10 border border-orange-400/30 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${PLATFORM_ICONS[GENERATED_AD.platform] ?? 'ri-global-line'} text-orange-400 text-base`} />
                  </div>
                  <span className="text-orange-300 text-xs font-bold uppercase tracking-wider">{GENERATED_AD.platform}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-bar-chart-2-fill text-green-400 text-sm" />
                  </div>
                  <span className="text-green-400 text-xs font-bold">Score {GENERATED_AD.score}/100</span>
                </div>
              </div>
              <p className="text-white font-bold text-sm leading-snug">{GENERATED_AD.headline}</p>
              <p className="text-white/70 text-xs leading-relaxed">{GENERATED_AD.body}</p>
              <span className="inline-block bg-orange-500/30 border border-orange-400/40 text-orange-300 text-xs font-bold px-3 py-1 rounded-full">
                {GENERATED_AD.cta} →
              </span>
            </div>
          </div>
        </div>
      </div>

      <p className="text-center text-white/40 text-xs mt-3 tracking-wide">
        ↑ Watch AI generate a real ad in seconds
      </p>
    </div>
  );
}

export default function HIWHero() {
  return (
    <header className="relative flex items-center justify-center overflow-hidden pt-16">
      <div className="absolute inset-0">
        <img
          src="https://storage.readdy-site.link/project_files/b82d3669-b3d5-44e8-8c89-634acf8d0274/feadd16b-36bc-4d7c-b847-cae121f2b5df_9bd40002638e673c4037ac4767f117d4.jpg?v=52dc7f5982fe1fac154daf74912cb0bd"
          alt=""
          className="w-full h-full object-cover object-top"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/75" />
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-6 lg:px-8 text-center py-16">
        <span className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-400/30 text-orange-300 text-xs font-bold px-4 py-1.5 rounded-full mb-8 backdrop-blur-sm uppercase tracking-widest">
          Step-by-Step Guide
        </span>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-6">
          How to Create Ads with AI —{' '}
          <span className="text-orange-400">Simple, Fast, and Free</span>
        </h1>

        <p className="text-lg text-stone-300 max-w-2xl mx-auto mb-10 leading-relaxed">
          No copywriting skills. No design experience. No marketing background. This guide shows
          you exactly how AdForge&apos;s <strong className="text-white">AI ad generator</strong> works —
          from your first input to a launch-ready ad in under 3 minutes.
        </p>

        <div className="flex flex-wrap justify-center gap-6 mb-10 text-sm text-stone-300">
          {[
            { icon: 'ri-time-line', label: '3 min to first ad' },
            { icon: 'ri-user-smile-line', label: 'No experience needed' },
            { icon: 'ri-global-line', label: '5 platforms supported' },
            { icon: 'ri-price-tag-3-line', label: 'Free to start' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${item.icon} text-orange-400 text-base`} />
              </div>
              <span className="text-sm font-medium">{item.label}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/signup"
            className="px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Try It Now — It&apos;s Free
          </Link>
          <Link
            to="/features"
            className="px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold text-base rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer backdrop-blur-sm"
          >
            See All Features
          </Link>
        </div>

        <AnimatedDemo />
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent" />
    </header>
  );
}
